import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import {
    cloneElement,
    isValidElement,
    useId,
    useState,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'

export interface TooltipProps extends ComponentPropsWithRef<'div'> {
    text?: string | null
    align?: 'left' | 'right'
    children: ReactNode
}

export function Tooltip({
    text,
    align = 'left',
    children,
    className = '',
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    ...restProps
}: TooltipProps) {
    const tooltipId = useId()
    const [showTooltip, setShowTooltip] = useState(false)
    const sideClass = align === 'left' ? 'right-0' : 'left-0'

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e)

        if (e.key === KeyboardKeyEnum.Escape) {
            setShowTooltip(false)
        }
    }

    let renderedChildren = children
    if (isValidElement(children) && text) {
        const existingDescribedBy = (children.props as any)['aria-describedby']

        const combinedDescribedBy =
            [existingDescribedBy, showTooltip ? tooltipId : undefined]
                .filter(Boolean)
                .join(' ') || undefined

        renderedChildren = cloneElement(children as React.ReactElement<any>, {
            'aria-describedby': combinedDescribedBy,
        })
    }

    return (
        <div
            {...restProps}
            aria-describedby={showTooltip && text ? tooltipId : undefined}
            className={`relative ${className}`.trim()}
            onMouseEnter={(e) => {
                setShowTooltip(true)
                onMouseEnter?.(e)
            }}
            onMouseLeave={(e) => {
                setShowTooltip(false)
                onMouseLeave?.(e)
            }}
            onFocus={(e) => {
                setShowTooltip(true)
                restProps.onFocus?.(e)
            }}
            onBlur={(e) => {
                setShowTooltip(false)
                restProps.onBlur?.(e)
            }}
            onKeyDown={handleKeyDown}
        >
            {showTooltip && text ? (
                <div
                    id={tooltipId}
                    role="tooltip"
                    onMouseEnter={() => setShowTooltip(false)}
                    className={`absolute -top-full px-2 py-1.5 bg-white border border-stone-200 text-gray-900 rounded-lg cursor-default text-xs z-10 whitespace-nowrap shadow-xs ${sideClass}`}
                >
                    {text}
                </div>
            ) : null}

            {renderedChildren}
        </div>
    )
}
